import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Zap, MessageCircle, BookOpen, Globe } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";

export default function CreditModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const handler = (e) => { setDetail(e.detail || {}); setOpen(true); };
    window.addEventListener("mel:credits-blocked", handler);
    return () => window.removeEventListener("mel:credits-blocked", handler);
  }, []);

  const code = detail?.code;
  const isPro = detail?.plan === "pro";
  const paused = code === "kill_switch" || code === "budget";
  const showUpgrade = !paused && !isPro;

  const benefits = [
    { icon: Zap, key: "paywall_unlimited" },
    { icon: MessageCircle, key: "up_feat_stories" },
    { icon: BookOpen, key: "up_feat_lessons" },
    { icon: Globe, key: "up_feat_langs" },
  ];

  // Paused / PRO cases: keep it simple and reassuring, no upgrade pitch.
  if (!showUpgrade) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[340px] rounded-3xl border-0 p-0 overflow-hidden" data-testid="credits-modal">
          <div className="flex flex-col items-center text-center px-6 pt-8 pb-6">
            <span className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-4"><Crown size={26} /></span>
            <h2 className="font-heading text-lg font-extrabold text-[#0B1727]" data-testid="credits-modal-title">{paused ? t("ai_paused_title") : t("ai_limit_title")}</h2>
            <p className="text-slate-500 text-sm mt-2 leading-relaxed">{paused ? t("ai_paused_body") : t("ai_limit_body")}</p>
            <button onClick={() => setOpen(false)} data-testid="credits-modal-close" className="mt-6 text-sm text-slate-400 font-medium press">{t("common_got_it")}</button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // FREE user hit the limit -> premium upgrade paywall.
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[360px] rounded-[28px] border-0 p-0 overflow-hidden bg-white" data-testid="credits-modal">
        {/* Hero */}
        <div className="relative px-6 pt-8 pb-7 text-center text-white overflow-hidden" style={{ background: "linear-gradient(135deg,#4f46e5 0%,#2563EB 55%,#22d3ee 130%)" }}>
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -left-12 bottom-0 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="relative mx-auto mb-3 w-16 h-16">
            <div className="w-16 h-16 rounded-[20px] bg-white flex items-center justify-center shadow-lg ring-1 ring-white/50">
              <div style={{ height: 30, width: 30 * (490 / 243),
                WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
                WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskPosition: "center", maskPosition: "center",
                background: "linear-gradient(135deg,#6aa6f7,#2f6fe0 62%,#17368f)" }} className="block" />
            </div>
            <span className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-white shadow" style={{ background: "linear-gradient(135deg,#fcd34d,#f59e0b 60%,#d97706)" }}>
              <Crown size={14} className="text-white" fill="currentColor" />
            </span>
          </div>
          <p className="relative text-[11px] font-extrabold tracking-[0.28em] text-white/80">{t("paywall_pro")}</p>
          <h2 className="relative font-heading text-2xl font-extrabold mt-1" data-testid="credits-modal-title">{t("ai_limit_title")}</h2>
        </div>

        {/* Benefits */}
        <div className="px-6 pt-5 pb-2 space-y-3">
          {benefits.map(({ icon: Icon, key }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="w-9 h-9 shrink-0 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Icon size={17} /></span>
              <span className="text-sm font-medium text-[#0B1727] flex-1">{t(key)}</span>
              <Check size={16} className="text-emerald-500 shrink-0" />
            </div>
          ))}
        </div>

        {/* Price + CTA */}
        <div className="px-6 pt-4 pb-7">
          <div className="flex items-end justify-center gap-1.5 mb-3">
            <span className="font-heading text-3xl font-extrabold text-[#0B1727]">{t("paywall_price")}</span>
            <span className="text-slate-400 text-sm font-medium mb-1">{t("paywall_per_month")}</span>
          </div>
          <button
            onClick={() => { setOpen(false); nav("/upgrade"); }}
            data-testid="credits-modal-cta"
            className="w-full text-white font-bold py-3.5 rounded-2xl press flex items-center justify-center gap-2 shadow-[0_16px_38px_-14px_rgba(37,99,235,0.95)]"
            style={{ background: "linear-gradient(90deg,#4f46e5,#2563EB)" }}
          >
            <Crown size={18} /> {t("upgrade_pro_cta")}
          </button>
          <p className="text-center text-[11px] text-slate-400 mt-2.5">{t("paywall_cancel")}</p>
          <button onClick={() => setOpen(false)} data-testid="credits-modal-close" className="w-full mt-3 text-sm text-slate-400 font-medium press">{t("paywall_maybe_later")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
